import React, { useEffect, useMemo, useState } from "react";
import { CPInput } from "../CPInput";
import { CPDropdown } from "../CPDropdown";
import { formatCurrency } from "../../helpers/estimateTotals";
import {
  getAddableInvoiceLineCategories,
  INVOICE_LINE_TYPE,
} from "../../helpers/invoiceLineTypes";
import {
  loadImportableLabourCandidates,
  loadImportableMaterialCandidates,
} from "../../helpers/invoiceLineImport";
import {
  buildManualInvoiceLine,
  buildServiceLineFromCatalog,
  createInvoiceLineId,
} from "../../helpers/invoiceReview";
import {
  findInvoiceServiceById,
  loadInvoiceServiceCatalog,
} from "../../helpers/invoiceServiceCatalog";
import { formatSupabaseError } from "../../helpers/estimates";

import { ViewportDialog } from "../shared/ViewportDialog";

const SHEET_STYLE = {
  "--cp-vd-max-width": "520px",
};

const ACTION_BTN = {
  padding: "10px 16px",
  borderRadius: "10px",
  border: "1px solid rgba(201,168,76,0.45)",
  background: "rgba(201,168,76,0.12)",
  color: "#c9a84c",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
};

const CATEGORY_BTN_STYLE = {
  ...ACTION_BTN,
  width: "100%",
  textAlign: "left",
  marginBottom: "10px",
  padding: "14px 16px",
};

const CANDIDATE_ROW_STYLE = {
  display: "flex",
  gap: "10px",
  alignItems: "flex-start",
  padding: "10px 0",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
};

const CHECKBOX_STYLE = {
  width: "18px",
  height: "18px",
  accentColor: "#c9a84c",
  cursor: "pointer",
  marginTop: "2px",
};

function emptyServiceForm(service = null) {
  return {
    serviceId: service?.service_id || "",
    name: service?.name || "",
    description: service?.description || "",
    quantity: String(service?.default_quantity ?? 1),
    unitPrice: service?.default_unit_price != null
      ? String(service.default_unit_price)
      : "",
    taxable: service?.taxable !== false,
  };
}

function emptyLabourForm() {
  return {
    description: "",
    detail: "",
    quantity: "",
    unitAmount: "",
    taxable: true,
  };
}

/**
 * Add Line Item modal — category picker + type-specific flows.
 * Extra categories can be enabled via INVOICE_LINE_TYPE_REGISTRY.
 */
export function AddInvoiceLineItemModal({
  open,
  actor,
  invoice,
  lineItems,
  onClose,
  onAddLines,
}) {
  const categories = useMemo(() => getAddableInvoiceLineCategories(), []);
  const [step, setStep] = useState("category");
  const [selectedType, setSelectedType] = useState(null);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [serviceForm, setServiceForm] = useState(() => emptyServiceForm());
  const [labourForm, setLabourForm] = useState(() => emptyLabourForm());
  const [candidates, setCandidates] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep("category");
    setSelectedType(null);
    setServiceForm(emptyServiceForm());
    setLabourForm(emptyLabourForm());
    setCandidates([]);
    setSelectedKeys({});
    setError("");
    setLoading(false);
  }, [open]);

  useEffect(() => {
    if (!open || selectedType !== INVOICE_LINE_TYPE.SERVICE) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const catalog = await loadInvoiceServiceCatalog();
        if (!cancelled) setServiceCatalog(catalog);
      } catch (err) {
        if (!cancelled) setError(formatSupabaseError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, selectedType]);

  useEffect(() => {
    if (
      !open ||
      (step !== INVOICE_LINE_TYPE.LABOUR && step !== INVOICE_LINE_TYPE.MATERIAL)
    ) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");
      setCandidates([]);
      setSelectedKeys({});
      try {
        const loader =
          step === INVOICE_LINE_TYPE.LABOUR
            ? loadImportableLabourCandidates
            : loadImportableMaterialCandidates;
        const rows = await loader(actor, invoice, lineItems);
        if (!cancelled) setCandidates(rows);
      } catch (err) {
        if (!cancelled) setError(formatSupabaseError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, step, actor, invoice, lineItems]);

  if (!open) return null;

  function selectCategory(category) {
    setSelectedType(category.id);
    if (category.id === INVOICE_LINE_TYPE.LABOUR) {
      setStep("labour_chooser");
    } else {
      setStep(category.id);
    }
    setError("");
  }

  function handleServiceCatalogChange(serviceId) {
    const service = findInvoiceServiceById(serviceCatalog, serviceId);
    setServiceForm(emptyServiceForm(service || { service_id: serviceId }));
  }

  function canSubmitService() {
    if (!serviceForm.name.trim()) return false;
    if (serviceForm.quantity === "" || Number.isNaN(Number(serviceForm.quantity))) {
      return false;
    }
    if (serviceForm.unitPrice === "" || Number.isNaN(Number(serviceForm.unitPrice))) {
      return false;
    }
    return true;
  }

  function handleAddService() {
    if (!canSubmitService()) return;
    const service = findInvoiceServiceById(
      serviceCatalog,
      serviceForm.serviceId,
    );
    const line = buildServiceLineFromCatalog(service, {
      name: serviceForm.name,
      description: serviceForm.description,
      quantity: Number(serviceForm.quantity),
      unitPrice: Number(serviceForm.unitPrice),
      taxable: serviceForm.taxable,
      sortOrder: (lineItems?.length || 0),
      lineId: createInvoiceLineId(),
    });
    onAddLines?.([line]);
    onClose?.();
  }

  function canSubmitManualLabour() {
    if (!labourForm.description.trim()) return false;
    if (labourForm.quantity === "" || Number.isNaN(Number(labourForm.quantity))) {
      return false;
    }
    if (
      labourForm.unitAmount === "" ||
      Number.isNaN(Number(labourForm.unitAmount))
    ) {
      return false;
    }
    return true;
  }

  function handleAddManualLabour() {
    if (!canSubmitManualLabour()) return;
    const line = buildManualInvoiceLine({
      lineType: INVOICE_LINE_TYPE.LABOUR,
      description: labourForm.description,
      detail: labourForm.detail,
      quantity: Number(labourForm.quantity),
      unitLabel: "hrs",
      unitAmount: Number(labourForm.unitAmount),
      taxable: labourForm.taxable,
      sortOrder: lineItems?.length || 0,
      lineId: createInvoiceLineId(),
    });
    onAddLines?.([line]);
    onClose?.();
  }

  function toggleCandidate(key) {
    setSelectedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function toggleAllCandidates() {
    const allSelected =
      candidates.length > 0 &&
      candidates.every((row) => selectedKeys[row.key]);
    if (allSelected) {
      setSelectedKeys({});
      return;
    }
    const next = {};
    for (const row of candidates) next[row.key] = true;
    setSelectedKeys(next);
  }

  function handleAddImportCandidates() {
    const selected = candidates.filter((row) => selectedKeys[row.key]);
    if (!selected.length) return;

    const baseSort = lineItems?.length || 0;
    const lines = selected.map((row, index) => ({
      ...row.seedLine,
      line_id: createInvoiceLineId(),
      sort_order: baseSort + index,
    }));
    onAddLines?.(lines);
    onClose?.();
  }

  const selectedCandidateCount = Object.keys(selectedKeys).filter(
    (key) => selectedKeys[key],
  ).length;
  const allCandidatesSelected =
    candidates.length > 0 &&
    candidates.every((row) => selectedKeys[row.key]);

  return (
    <ViewportDialog
      presentation="sheet"
      onClose={onClose}
      label="Add Line Item"
      panelStyle={SHEET_STYLE}
    >
        {step === "category" && (
          <>
            <h3 style={{ marginTop: 0, marginBottom: "8px" }}>Add Line Item</h3>
            <p style={{ opacity: 0.75, marginTop: 0, marginBottom: "16px" }}>
              Choose a line type to add to this invoice draft.
            </p>
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                style={CATEGORY_BTN_STYLE}
                onClick={() => selectCategory(category)}
              >
                <div style={{ fontWeight: 700 }}>{category.label}</div>
                <div style={{ opacity: 0.7, fontSize: "12px", marginTop: "4px" }}>
                  {category.description}
                </div>
              </button>
            ))}
            <button
              type="button"
              className="nx-btn"
              style={{ ...ACTION_BTN, width: "100%", marginTop: "8px" }}
              onClick={onClose}
            >
              Cancel
            </button>
          </>
        )}

        {step === INVOICE_LINE_TYPE.SERVICE && (
          <>
            <h3 style={{ marginTop: 0 }}>Add Service</h3>
            {error ? (
              <p style={{ color: "#e06b5a" }}>{error}</p>
            ) : null}
            {loading ? (
              <p style={{ opacity: 0.7 }}>Loading service catalog…</p>
            ) : (
              <>
                <div style={{ marginBottom: "12px" }}>
                  <CPDropdown
                    label="Service catalog"
                    value={serviceForm.serviceId}
                    onChange={handleServiceCatalogChange}
                    placeholder="Custom service…"
                    options={serviceCatalog.map((service) => ({
                      value: service.service_id,
                      label: service.name,
                    }))}
                  />
                </div>

                <CPInput
                  label="Name"
                  value={serviceForm.name}
                  onChange={(value) =>
                    setServiceForm((prev) => ({ ...prev, name: value }))
                  }
                  placeholder="Service name"
                />
                <CPInput
                  label="Description"
                  value={serviceForm.description}
                  onChange={(value) =>
                    setServiceForm((prev) => ({ ...prev, description: value }))
                  }
                  placeholder="Optional detail"
                />
                <CPInput
                  label="Quantity"
                  value={serviceForm.quantity}
                  onChange={(value) =>
                    setServiceForm((prev) => ({ ...prev, quantity: value }))
                  }
                  placeholder="1"
                />
                <CPInput
                  label="Unit price"
                  value={serviceForm.unitPrice}
                  onChange={(value) =>
                    setServiceForm((prev) => ({ ...prev, unitPrice: value }))
                  }
                  placeholder="0.00"
                />
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    margin: "8px 0 16px",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={serviceForm.taxable}
                    onChange={(e) =>
                      setServiceForm((prev) => ({
                        ...prev,
                        taxable: e.target.checked,
                      }))
                    }
                    style={CHECKBOX_STYLE}
                  />
                  Taxable
                </label>
              </>
            )}
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="button"
                className="nx-btn"
                style={{ flex: 1 }}
                onClick={() => {
                  setStep("category");
                  setSelectedType(null);
                }}
              >
                Back
              </button>
              <button
                type="button"
                className="nx-btn nx-btn-gold"
                style={{ flex: 1 }}
                disabled={loading || !canSubmitService()}
                onClick={handleAddService}
              >
                Add Service
              </button>
            </div>
          </>
        )}

        {step === "labour_chooser" && (
          <>
            <h3 style={{ marginTop: 0 }}>Add Labour</h3>
            <p style={{ opacity: 0.75, marginTop: 0 }}>
              Import from unallocated time logs, or enter labour hours manually
              without creating a time log.
            </p>
            <button
              type="button"
              style={CATEGORY_BTN_STYLE}
              onClick={() => setStep(INVOICE_LINE_TYPE.LABOUR)}
            >
              <div style={{ fontWeight: 700 }}>Import from Time Logs</div>
              <div style={{ opacity: 0.75, fontSize: "13px", marginTop: 4 }}>
                Select uninvoiced labour already recorded on this project.
              </div>
            </button>
            <button
              type="button"
              style={CATEGORY_BTN_STYLE}
              onClick={() => {
                setLabourForm(emptyLabourForm());
                setStep("labour_manual");
              }}
            >
              <div style={{ fontWeight: 700 }}>Manual Labour</div>
              <div style={{ opacity: 0.75, fontSize: "13px", marginTop: 4 }}>
                Enter description, hours, and rate. No time log is created.
              </div>
            </button>
            <div style={{ display: "flex", gap: "10px", marginTop: 8 }}>
              <button
                type="button"
                className="nx-btn"
                style={{ flex: 1 }}
                onClick={() => {
                  setStep("category");
                  setSelectedType(null);
                }}
              >
                Back
              </button>
            </div>
          </>
        )}

        {step === "labour_manual" && (
          <>
            <h3 style={{ marginTop: 0 }}>Manual Labour</h3>
            <p style={{ opacity: 0.75, marginTop: 0 }}>
              Bill labour without linking a time log. Refresh from Project keeps
              these lines.
            </p>
            {error ? <p style={{ color: "#e06b5a" }}>{error}</p> : null}
            <div className="rb-field-group">
              <CPInput
                label="Description"
                value={labourForm.description}
                onChange={(value) =>
                  setLabourForm((prev) => ({ ...prev, description: value }))
                }
              />
            </div>
            <div className="rb-field-group">
              <CPInput
                label="Detail (optional)"
                value={labourForm.detail}
                onChange={(value) =>
                  setLabourForm((prev) => ({ ...prev, detail: value }))
                }
              />
            </div>
            <div className="rb-field-group">
              <CPInput
                label="Qty / hours"
                type="number"
                value={labourForm.quantity}
                onChange={(value) =>
                  setLabourForm((prev) => ({ ...prev, quantity: value }))
                }
              />
            </div>
            <div className="rb-field-group">
              <CPInput
                label="Unit rate"
                type="number"
                value={labourForm.unitAmount}
                onChange={(value) =>
                  setLabourForm((prev) => ({ ...prev, unitAmount: value }))
                }
              />
            </div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "16px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={labourForm.taxable}
                onChange={(event) =>
                  setLabourForm((prev) => ({
                    ...prev,
                    taxable: event.target.checked,
                  }))
                }
                style={CHECKBOX_STYLE}
              />
              Taxable
            </label>
            <p style={{ opacity: 0.7, fontSize: "13px" }}>
              Amount:{" "}
              {formatCurrency(
                (Number(labourForm.quantity) || 0) *
                  (Number(labourForm.unitAmount) || 0),
              )}
            </p>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="button"
                className="nx-btn"
                style={{ flex: 1 }}
                onClick={() => setStep("labour_chooser")}
              >
                Back
              </button>
              <button
                type="button"
                className="nx-btn nx-btn-gold"
                style={{ flex: 1 }}
                disabled={!canSubmitManualLabour()}
                onClick={handleAddManualLabour}
              >
                Add Labour
              </button>
            </div>
          </>
        )}

        {(step === INVOICE_LINE_TYPE.LABOUR ||
          step === INVOICE_LINE_TYPE.MATERIAL) && (
          <>
            <h3 style={{ marginTop: 0 }}>
              Import {step === INVOICE_LINE_TYPE.LABOUR ? "Labour" : "Materials"}
            </h3>
            <p style={{ opacity: 0.75, marginTop: 0 }}>
              Select uninvoiced{" "}
              {step === INVOICE_LINE_TYPE.LABOUR ? "labour" : "materials"} from
              this project to add to the draft. Source records are not deleted.
            </p>
            {error ? (
              <p style={{ color: "#e06b5a" }}>{error}</p>
            ) : null}
            {loading ? (
              <p style={{ opacity: 0.7 }}>Loading…</p>
            ) : candidates.length === 0 ? (
              <p style={{ opacity: 0.7 }}>
                No unallocated{" "}
                {step === INVOICE_LINE_TYPE.LABOUR ? "labour" : "materials"}{" "}
                available to import.
              </p>
            ) : (
              <>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "8px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allCandidatesSelected}
                    onChange={toggleAllCandidates}
                    style={CHECKBOX_STYLE}
                  />
                  Select all ({candidates.length})
                </label>
                <div style={{ marginBottom: "16px" }}>
                  {candidates.map((row) => (
                    <label key={row.key} style={CANDIDATE_ROW_STYLE}>
                      <input
                        type="checkbox"
                        checked={!!selectedKeys[row.key]}
                        onChange={() => toggleCandidate(row.key)}
                        style={CHECKBOX_STYLE}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{row.label}</div>
                        <div style={{ opacity: 0.65, fontSize: "12px" }}>
                          {row.detail}
                        </div>
                      </div>
                      <div style={{ color: "#c9a84c", fontWeight: 700 }}>
                        {formatCurrency(row.seedLine.line_total)}
                      </div>
                    </label>
                  ))}
                </div>
              </>
            )}
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="button"
                className="nx-btn"
                style={{ flex: 1 }}
                onClick={() => {
                  if (step === INVOICE_LINE_TYPE.LABOUR) {
                    setStep("labour_chooser");
                  } else {
                    setStep("category");
                    setSelectedType(null);
                  }
                }}
              >
                Back
              </button>
              <button
                type="button"
                className="nx-btn nx-btn-gold"
                style={{ flex: 1 }}
                disabled={loading || selectedCandidateCount === 0}
                onClick={handleAddImportCandidates}
              >
                Add Selected ({selectedCandidateCount})
              </button>
            </div>
          </>
        )}
    </ViewportDialog>
  );
}
